import {FormLabelMechanicalEnum, FormLabelMechanicalGroupEnum, FormLabelType} from "../Types";
import {
  getFormLabelMechanicalEnumFromGroup,
  isDiscountEuro,
  hasDiscountField,
  isDiscountPercent,
  hasOldPriceField,
  hasDiscountPrecision, isOldPriceEuro, isOldPricePercent,
} from "../Config";
import {
  PdfLabelCalculationType,
  PdfLabelCommonType,
  PdfLabelIconType,
  PdfLabelSimpleType,
  PdfLabelTypeEnum
} from "../Types/Pdf";
import {getPriceFromNumber} from "../../../Utils";

export const getPdfLabelFromFormLabel = (label: FormLabelType): PdfLabelSimpleType | PdfLabelIconType | PdfLabelCalculationType => {
  return getConvertorFunction(label.mechanical)(label);
}

const getConvertorFunction = (mechanical: FormLabelMechanicalEnum) => {
  if (!getFormLabelMechanicalEnumFromGroup(FormLabelMechanicalGroupEnum.LOT).includes(mechanical) && (
    (hasDiscountField(mechanical) && !hasOldPriceField(mechanical))
    || (!hasDiscountField(mechanical) && hasOldPriceField(mechanical))
  )) {
    return getPdfLabelSimpleFromFormLabel;
  }

  if (!hasDiscountField(mechanical) && !hasOldPriceField(mechanical) && hasDiscountPrecision(mechanical)) {
    return getPdfLabelIconFromFormLabel;
  }

  return getPdfLabelCalculationFromFormLabel;
}

const getPdfLabelCommonFromFormLabel = (label: FormLabelType): PdfLabelCommonType => {
  let details = label.discountDetail;

  let discountLimit;
  if (label.discountLimit){
    if (details) {
      details += ", ";
    }

    if (label.discountLimit[0].toLocaleDateString() === label.discountLimit[1].toLocaleDateString()) {
      discountLimit = "jusqu'au " + label.discountLimit[0].toLocaleDateString();
    } else {
      discountLimit = "du " + label.discountLimit[0].toLocaleDateString() + " au " + label.discountLimit[1].toLocaleDateString();
    }

    if (!details) {
      discountLimit = discountLimit.charAt(0).toUpperCase() + discountLimit.slice(1);
    }

    details += discountLimit;
  }
  details = details ? details + "." : "";

  return {
    type: null,
    mechanical: label.mechanical,
    title: label.title,
    banner: label.banner,
    icon: label.icon,
    name: label.name,
    format: label.capacity,
    details,
  }
}

const getPdfLabelSimpleFromFormLabel = (label: FormLabelType): PdfLabelSimpleType => {
  let discount = '';

  if (hasDiscountField(label.mechanical)) {
    discount = '-' + label.discount.replace('.', ',');
    isDiscountEuro(label.mechanical) && (discount += '€');
    isDiscountPercent(label.mechanical) && (discount += '%');
  }

  if (hasOldPriceField(label.mechanical)) {
    discount = label.oldPrice.replace('.', ',');
    isOldPriceEuro(label.mechanical) && (discount += '€');
    isOldPricePercent(label.mechanical) && (discount += '%');
  }

  return {
    ...getPdfLabelCommonFromFormLabel(label),
    type: PdfLabelTypeEnum.SIMPLE,
    discount,
    discountDetail: getPdfSimpleDiscountDetail(label.mechanical),
  }
}

const getPdfLabelIconFromFormLabel = (label: FormLabelType): PdfLabelIconType => {
  let icon = 'Cadeau';

  return {
    ...getPdfLabelCommonFromFormLabel(label),
    type: PdfLabelTypeEnum.ICON,
    icon,
    discountDetail: label.discountPrecision,
  }
}

const getPdfSimpleDiscountDetail = (label: FormLabelMechanicalEnum): string => {
  switch (label) {
    case FormLabelMechanicalEnum.SPECIAL_ROUND:
      return '';
    case FormLabelMechanicalEnum.SPECIAL_ALL_OFF_EURO:
    case FormLabelMechanicalEnum.SPECIAL_ALL_OFF_PERCENT:
      return 'SUR TOUT';
    case FormLabelMechanicalEnum.RANGE_AMOUNT:
    case FormLabelMechanicalEnum.RANGE_PERCENT:
      return 'SUR LA GAMME';
    case FormLabelMechanicalEnum.BRAND_AMOUNT:
    case FormLabelMechanicalEnum.BRAND_PERCENT:
      return 'SUR LA MARQUE';
    case FormLabelMechanicalEnum.VOLUME_DIFFERENT_PERCENT_SECOND_PRODUCT:
      return 'SUR UN 2ÈME PRODUIT DIFFÉRENT';
    case FormLabelMechanicalEnum.VOLUME_SAME_RANGE_PERCENT_SECOND_PRODUCT:
      return 'SUR UN 2ÈME PRODUIT DE LA GAMME';
    case FormLabelMechanicalEnum.VOLUME_IDENTICAL_PERCENT_SECOND_PRODUCT:
      return 'SUR UN 2ÈME PRODUIT IDENTIQUE';
    case FormLabelMechanicalEnum.VOLUME_DIFFERENT_PERCENT_THIRD_PRODUCT:
      return 'SUR UN 3ÈME PRODUIT DIFFÉRENT';
    case FormLabelMechanicalEnum.VOLUME_SAME_RANGE_PERCENT_THIRD_PRODUCT:
      return 'SUR UN 3ÈME PRODUIT DE LA GAMME';
    case FormLabelMechanicalEnum.VOLUME_IDENTICAL_PERCENT_THIRD_PRODUCT:
      return 'SUR UN 3ÈME PRODUIT IDENTIQUE';
    default:
      return "";
  }
}

const getPdfLabelCalculationFromFormLabel = (label: FormLabelType): PdfLabelCalculationType => {
  let discountUnitPrice = null;
  let originalUnitPrice = null;
  let discountDetail = null;

  const prices = calculatePrices(label);

  if (prices.discountUnitPrice) {
    const discountUnitPriceObject = getPriceFromNumber(prices.discountUnitPrice);
    discountUnitPrice = 'SOIT ' + discountUnitPriceObject.whole + '€' + discountUnitPriceObject.decimals + ' L\'UNITE';
  }

  if (prices.originalUnitPrice) {
    const originalUnitPriceObject = getPriceFromNumber(prices.originalUnitPrice);
    originalUnitPrice = 'AU LIEU DE ' + originalUnitPriceObject.whole + '€' + originalUnitPriceObject.decimals;
  }

  if (label.mechanical === FormLabelMechanicalEnum.BRI_PERCENT && prices.discountPrice && prices.originalPrice) {
    const discountDetailDiscountValue = getPriceFromNumber(prices.originalPrice - prices.discountPrice);
    discountDetail = 'SOIT ' + discountDetailDiscountValue.whole + '€' + discountDetailDiscountValue.decimals + ' d\'économie';
  }

  return {
    ...getPdfLabelCommonFromFormLabel(label),
    type: PdfLabelTypeEnum.CALCULATION,
    discountPrice: getPriceFromNumber(prices.discountPrice),
    discountUnitPrice,
    originalPrice: getPriceFromNumber(prices.originalPrice),
    originalUnitPrice,
    discountDetail,
  }
}

const calculatePrices = (label: FormLabelType) => {
  let originalPrice: number | null = null;
  let originalUnitPrice: number | null = null;
  let discountPrice: number | null = null;
  let discountUnitPrice: number | null = null;

  if (hasDiscountField(label.mechanical) && hasOldPriceField(label.mechanical)) {
    if (label.oldPrice !== '' && label.discount !== '') {
      originalPrice = parseFloat(label.oldPrice.replace(',', '.'));
      const discount = parseFloat(label.discount.replace(',', '.'));

      if (isDiscountPercent(label.mechanical)) {
        discountPrice = (originalPrice - (originalPrice * discount / 100));
      }

      if (isDiscountEuro(label.mechanical)) {
        discountPrice = (originalPrice - discount);
      }
    }
  }

  // LOT
  if (getFormLabelMechanicalEnumFromGroup(FormLabelMechanicalGroupEnum.LOT).includes(label.mechanical)) {
    if (label.oldPrice === '') {
      return {
        originalPrice,
        originalUnitPrice,
        discountPrice,
        discountUnitPrice,
      }
    }

    let numberItem;
    switch (label.mechanical) {
      case FormLabelMechanicalEnum.LOT_TWO_PLUS_ONE:
        numberItem = 2;
        break;
      case FormLabelMechanicalEnum.LOT_THREE_PLUS_ONE:
        numberItem = 3;
        break;
      case FormLabelMechanicalEnum.LOT_FOUR_PLUS_ONE:
        numberItem = 4;
        break;
      default:
        numberItem = 1;
    }

    let numberFreeItem;
    switch (label.mechanical) {
      case FormLabelMechanicalEnum.LOT_TWO_PLUS_ONE:
      case FormLabelMechanicalEnum.LOT_THREE_PLUS_ONE:
      case FormLabelMechanicalEnum.LOT_FOUR_PLUS_ONE:
        numberFreeItem = 1;
        break;
      default:
        numberFreeItem = 0;
    }

    originalUnitPrice = parseFloat(label.oldPrice.replace(',', '.'));
    originalPrice = originalUnitPrice * (numberItem + numberFreeItem);
    discountPrice = originalUnitPrice * numberItem;
    discountUnitPrice = discountPrice / (numberItem + numberFreeItem);
  }

  return {
    originalPrice,
    originalUnitPrice,
    discountPrice,
    discountUnitPrice,
  }
}
